import { useMemo } from "react";
import { Icon } from "@/components/ui/Icon";
import { useMapSummary, type MapSummaryRow } from "@/lib/api/map";
import { STATUS_LABELS } from "@/lib/labels";
import "./map.css";

/**
 * Xarita boshidagi raqamlar.
 *
 * IKKI XIL FOIZ bor va ular maxraji bilan farq qiladi:
 *
 *   - KATAK ulushi (band, bloklangan) — maxraji o'sha vazifadagi
 *     kataklar soni;
 *   - TO'LDIRILISH — maxraji pallet o'rinlari soni. Bitta katakda
 *     to'rttagacha o'rin bo'lishi mumkin, shuning uchun «band
 *     katak» bilan «band o'rin» bir xil narsa emas.
 *
 * Shuning uchun ular alohida ustunda va sarlavhalari boshqacha.
 */

/** Ombor oqimi tartibi: qabul → saqlash → terish → jo'natish. */
const ORDER = [
  "RECEIPT",
  "BUFFER",
  "MX",
  "OX",
  "CONTROL",
  "SHIPPING",
  "YELLOW",
  "RED",
  "RETURN",
  "CUSTOMS",
  "DEFECT",
  "PRODUCTION",
];

const label = (value: string) =>
  STATUS_LABELS[value] ?? (value === "—" ? "Belgilanmagan" : value);

/** Ulush foizi. Maxraj nol bo'lsa foiz YO'Q — nol emas. */
function share(value: number, total: number): string {
  if (total <= 0) return "—";
  return `${((value / total) * 100).toFixed(1)}%`;
}

const number = (value: number) => value.toLocaleString("uz");

export function MapSummary({ warehouse }: { warehouse?: string }) {
  const summary = useMapSummary(warehouse);

  const rows = useMemo(() => {
    const items = summary.data?.items ?? [];
    return [...items].sort((left, right) => {
      const leftIndex = ORDER.indexOf(left.function);
      const rightIndex = ORDER.indexOf(right.function);
      return (
        (leftIndex === -1 ? ORDER.length : leftIndex) -
          (rightIndex === -1 ? ORDER.length : rightIndex) ||
        left.function.localeCompare(right.function)
      );
    });
  }, [summary.data]);

  if (summary.isLoading) {
    return <div className="skeleton" style={{ height: 180 }} />;
  }
  if (rows.length === 0) return null;

  const total = rows.reduce<MapSummaryRow>(
    (sum, row) => ({
      function: "JAMI",
      cell_count: sum.cell_count + row.cell_count,
      occupied_cells: sum.occupied_cells + row.occupied_cells,
      inactive_cells: sum.inactive_cells + row.inactive_cells,
      blocked_cells: sum.blocked_cells + row.blocked_cells,
      pallet_capacity: sum.pallet_capacity + row.pallet_capacity,
      occupied_slots: sum.occupied_slots + row.occupied_slots,
    }),
    {
      function: "JAMI", cell_count: 0, occupied_cells: 0, inactive_cells: 0,
      blocked_cells: 0, pallet_capacity: 0, occupied_slots: 0,
    },
  );

  const fill =
    total.pallet_capacity > 0
      ? (total.occupied_slots / total.pallet_capacity) * 100
      : 0;

  return (
    <section className="map-summary">
      {/* Umumiy to'lganlik — eng katta raqam: ombor rahbari
          birinchi navbatda shuni so'raydi. */}
      <div className="map-total">
        <div className="map-total-value num">
          {total.pallet_capacity > 0 ? `${fill.toFixed(1)}%` : "—"}
        </div>
        <div className="map-total-label">Ombor to'lgan</div>
        <div className="map-total-track" aria-hidden="true">
          <span className="map-total-bar" style={{ width: `${Math.min(fill, 100)}%` }} />
        </div>
        <div className="map-total-note">
          {number(total.occupied_slots)} / {number(total.pallet_capacity)} pallet o'rni
        </div>
        {total.blocked_cells > 0 && (
          <div className="map-total-warn">
            <Icon name="block" size={12} />
            {number(total.blocked_cells)} katak bloklangan ·{" "}
            {share(total.blocked_cells, total.cell_count)}
          </div>
        )}
      </div>

      <div className="table-scroll">
        <table className="data map-summary-table">
          <thead>
            <tr>
              <th>Vazifa</th>
              <th className="right">Katak</th>
              <th className="right">Band katak</th>
              <th className="right">Bloklangan</th>
              <th className="right">O'chirilgan</th>
              <th className="right">Pallet o'rni</th>
              <th className="right">To'ldirilish</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <SummaryRow key={row.function} row={row} />
            ))}
          </tbody>
          <tfoot>
            <SummaryRow row={total} total />
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function SummaryRow({ row, total }: { row: MapSummaryRow; total?: boolean }) {
  const fill =
    row.pallet_capacity > 0 ? (row.occupied_slots / row.pallet_capacity) * 100 : 0;

  return (
    <tr className={total ? "map-summary-total" : undefined}>
      <td>{total ? "Jami" : label(row.function)}</td>
      <td className="right num">{number(row.cell_count)}</td>
      <td className="right num">
        {number(row.occupied_cells)}
        <span className="map-share">{share(row.occupied_cells, row.cell_count)}</span>
      </td>
      <td className="right num">
        {row.blocked_cells > 0 ? (
          <>
            <span className="over">{number(row.blocked_cells)}</span>
            <span className="map-share">{share(row.blocked_cells, row.cell_count)}</span>
          </>
        ) : (
          <span className="muted">0</span>
        )}
      </td>
      <td className="right num">
        {row.inactive_cells > 0 ? number(row.inactive_cells) : <span className="muted">0</span>}
      </td>
      <td className="right num">
        {/* Mayda terish yacheykasida pallet o'rni YO'Q (TZ §5.2):
            nol sig'im «to'la» degani emas. */}
        {row.pallet_capacity > 0 ? (
          `${number(row.occupied_slots)} / ${number(row.pallet_capacity)}`
        ) : (
          <span className="muted">pallet emas</span>
        )}
      </td>
      <td className="right num">
        {row.pallet_capacity > 0 ? `${fill.toFixed(1)}%` : <span className="muted">—</span>}
      </td>
    </tr>
  );
}
